import { store } from "@/app/shared/stores/store";
import { realtimeBaseUrl, realtimePath } from "@/environment/environment";
import type { WorkerToTabMessage } from "@/app/shared/services/realtime/realtime-worker";
import { Channel } from "../types";

/**
 * The socket's reachability as last reported by the worker, cached per tab and not per
 * channel instance: the SharedWorker owns one single socket for the whole tab. Once it
 * has told one channel that the realtime server cannot be reached - the normal case in
 * local dev, and for every non-admin user - the next channel gets that answer
 * immediately instead of waiting for a connect timeout of its own.
 */
let lastKnownReachability: boolean | null = null;

export abstract class BaseChannel {
  abstract readonly channelName: Channel;

  protected readonly port: MessagePort;

  private readonly userIdPromise: Promise<string>;

  private readonly reachabilityListeners = new Set<(reachable: boolean) => void>();

  constructor() {
    const worker = new SharedWorker(
      new URL("../realtime-worker.ts", import.meta.url),
      { name: "realtime-worker" },
    );
    this.port = worker.port;

    let resolveUserId!: (id: string) => void;
    this.userIdPromise = new Promise(resolve => { resolveUserId = resolve; });

    this.port.addEventListener("message", (event: MessageEvent<WorkerToTabMessage>) => {
      if (event.data.type === "connected") {
        resolveUserId(event.data.userId);
        this.onReachabilityReported(true);
      } else if (event.data.type === "unavailable") {
        this.onReachabilityReported(false);
      }
    });

    this.port.start();
    this.port.postMessage({
      type: "init",
      token: store.auth.token,
      botProtectionToken: store.protection.botProtectionToken,
      realtimeBaseUrl,
      realtimePath: realtimePath ?? "",
    });
  }

  protected get userId(): Promise<string> {
    return this.userIdPromise;
  }

  /**
   * Resolves with the state of the shared socket: true once the worker reports a
   * connected socket, false once it reports that the realtime server cannot be reached,
   * and false after `timeoutMs` if the worker stays silent (e.g. a handshake that is
   * never answered). Channels use it to fall back to HTTP instead of awaiting a
   * subscription that would never be confirmed.
   */
  protected isRealtimeReachable(timeoutMs: number): Promise<boolean> {
    if (lastKnownReachability !== null) {
      return Promise.resolve(lastKnownReachability);
    }

    return new Promise<boolean>(resolve => {
      const timer = setTimeout(() => {
        this.reachabilityListeners.delete(listener);
        resolve(false);
      }, timeoutMs);

      const listener = (reachable: boolean) => {
        clearTimeout(timer);
        this.reachabilityListeners.delete(listener);
        resolve(reachable);
      };

      this.reachabilityListeners.add(listener);
    });
  }

  private onReachabilityReported(reachable: boolean): void {
    lastKnownReachability = reachable;
    for (const listener of [...this.reachabilityListeners]) {
      listener(reachable);
    }
  }

  protected async joinChannel(id: string): Promise<void> {
    const uid = await this.userIdPromise;
    this.port.postMessage({ type: "subscribe", channel: this.channelName, id, userId: uid });
  }

  protected async leaveChannel(id: string): Promise<void> {
    const uid = await this.userIdPromise;
    this.port.postMessage({ type: "unsubscribe", channel: this.channelName, id, userId: uid });
  }

  protected broadcastToTabs(key: string, payload: unknown): void {
    this.port.postMessage({ type: "broadcast", key, payload });
  }

  protected close(): void {
    this.port.close();
  }
}